import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Table from '../../components/UI/Table';
import Modal from '../../components/UI/Modal';
import HelpTooltip from '../../components/UI/HelpTooltip';
import { membersAPI, regionsAPI, memberCategoriesAPI, reportsAPI } from '../../services/api';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  CheckCircle, 
  XCircle,
  RotateCcw,
  FileSpreadsheet,
  FileText
} from 'lucide-react';
import { toast } from 'react-toastify';

const MEMBER_COLUMN_OPTIONS = [
  { value: 'member_id', label: 'Member ID' },
  { value: 'registration_no', label: 'Registration No' },
  { value: 'first_name', label: 'First Name' },
  { value: 'middle_name', label: 'Middle Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'alternate_phone', label: 'Alternate Phone' },
  { value: 'gender', label: 'Gender' },
  { value: 'date_of_birth', label: 'Date of Birth' },
  { value: 'nationality', label: 'Nationality' },
  { value: 'region', label: 'Region' },
  { value: 'city', label: 'City' },
  { value: 'address', label: 'Address' },
  { value: 'postal_code', label: 'Postal Code' },
  { value: 'occupation', label: 'Occupation' },
  { value: 'organization_name', label: 'Organization' },
  { value: 'organization_type', label: 'Sector/Organization Type' },
  { value: 'position_title', label: 'Position' },
  { value: 'years_of_experience', label: 'Years of Experience' },
  { value: 'membership_type', label: 'Membership Type' },
  { value: 'membership_status', label: 'Membership Status' },
  { value: 'approval_status', label: 'Approval Status' },
  { value: 'membership_date', label: 'Membership Date' },
  { value: 'member_category', label: 'Category' },
  { value: 'thematic_areas', label: 'Thematic Areas' },
  { value: 'languages_spoken', label: 'Languages Spoken' },
  { value: 'skills', label: 'Skills & Expertise' },
  { value: 'bio', label: 'Bio / Background' },
  { value: 'interested_trainings', label: 'Interested Trainings' },
  { value: 'has_previous_training', label: 'Has Previous Training' },
  { value: 'previous_trainings', label: 'Previous Training Details' },
  { value: 'emergency_contact_name', label: 'Emergency Contact Name' },
  { value: 'emergency_contact_phone', label: 'Emergency Contact Phone' },
  { value: 'emergency_contact_relationship', label: 'Emergency Contact Relationship' },
  { value: 'created_at', label: 'Created At' },
  { value: 'updated_at', label: 'Updated At' },
];

const MembersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.heading};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ExportActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const AvatarCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AvatarImage = styled.img`
  width: 44px;
  height: 44px;
  border-radius: ${props => props.theme.borderRadius.full};
  object-fit: cover;
  border: 2px solid ${props => props.theme.colors.primary + '40'};
`;

const AvatarFallback = styled.div`
  width: 44px;
  height: 44px;
  border-radius: ${props => props.theme.borderRadius.full};
  background: ${props => props.theme.colors.primary + '20'};
  color: ${props => props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  text-transform: uppercase;
`;

const AvatarDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  .name {
    font-weight: ${props => props.theme.typography.fontWeight.medium};
    color: ${props => props.theme.colors.text.primary};
  }

  .meta {
    font-size: ${props => props.theme.typography.fontSize.xs};
    color: ${props => props.theme.colors.text.secondary};
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 1rem;
  align-items: end;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  font-family: ${props => props.theme.typography.fontFamily.primary};
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  border: 1px solid ${props => props.theme.colors.border.medium};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.white};
  color: ${props => props.theme.colors.text.primary};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary + '20'};
  }
`;

const StatusBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background-color: ${props.theme.colors.success + '20'};
          color: ${props.theme.colors.success};
        `;
      case 'pending':
        return `
          background-color: ${props.theme.colors.warning + '20'};
          color: ${props.theme.colors.warning};
        `;
      case 'inactive':
        return `
          background-color: ${props.theme.colors.error + '20'};
          color: ${props.theme.colors.error};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.secondary};
        `;
    }
  }}
`;

const ApprovalBadge = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  
  ${props => {
    switch (props.status) {
      case 'central_pending':
      case 'regional_pending':
        return `
          background-color: ${props.theme.colors.warning + '20'};
          color: ${props.theme.colors.warning};
        `;
      case 'approved':
        return `
          background-color: ${props.theme.colors.success + '20'};
          color: ${props.theme.colors.success};
        `;
      case 'pending':
        return `
          background-color: ${props.theme.colors.warning + '20'};
          color: ${props.theme.colors.warning};
        `;
      case 'rejected':
        return `
          background-color: ${props.theme.colors.error + '20'};
          color: ${props.theme.colors.error};
        `;
      case 'draft':
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.secondary};
        `;
      default:
        return `
          background-color: ${props.theme.colors.gray[100]};
          color: ${props.theme.colors.text.secondary};
        `;
    }
  }}
`;

const STORAGE_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api').replace(/\/$/, '').replace(/\/api$/, '');

const getFileUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;
  return `${STORAGE_BASE_URL}/${normalizedPath.startsWith('storage/') ? normalizedPath : `storage/${normalizedPath}`}`;
};

const FieldRow = ({ label, value }) => (
  <div>
    <strong>{label}:</strong>{' '}
    {value && value !== '' && (!(Array.isArray(value)) || value.length > 0)
      ? Array.isArray(value) ? value.join(', ') : value
      : '—'}
  </div>
);

const FileLink = ({ label, path }) => {
  if (!path) {
    return <FieldRow label={label} value={null} />;
  }

  const url = getFileUrl(path);
  return (
    <div>
      <strong>{label}:</strong>{' '}
      <a href={url} target="_blank" rel="noopener noreferrer">View file</a>
    </div>
  );
};

const formatApprovalLabel = (status) => {
  if (!status) return 'Unknown';
  switch (status) {
    case 'draft':
      return 'Resubmission Requested';
    case 'central_pending':
      return 'Central Pending';
    case 'regional_pending':
      return 'Regional Pending';
    case 'approved':
      return 'Approved';
    case 'rejected':
      return 'Rejected';
    default:
      break;
  }
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const formatMembershipStatus = (status) => {
  if (!status) return 'Unknown';
  switch (status) {
    case 'active':
      return 'Active';
    case 'inactive':
      return 'Inactive';
    case 'pending':
      return 'Pending';
    case 'suspended':
      return 'Suspended';
    default:
      return status
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
  }
};

const Members = () => {
  const [members, setMembers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    region: '',
    status: '',
    category: ''
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showResubmitDialog, setShowResubmitDialog] = useState(false);
  const [resubmittingMember, setResubmittingMember] = useState(null);
  const [resubmitNotes, setResubmitNotes] = useState('');
  const [isResubmitting, setIsResubmitting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [selectedExportColumns, setSelectedExportColumns] = useState(MEMBER_COLUMN_OPTIONS.map(option => option.value));
  const [exporting, setExporting] = useState({ excel: false, pdf: false });
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  
  const toggleExportColumn = (column) => {
    setSelectedExportColumns(prev =>
      prev.includes(column)
        ? prev.filter(item => item !== column)
        : [...prev, column]
    );
  };

  const selectAllExportColumns = () => {
    setSelectedExportColumns(MEMBER_COLUMN_OPTIONS.map(option => option.value));
  };

  const clearExportColumns = () => {
    setSelectedExportColumns([]);
  };

  const hasSelectedExportColumns = selectedExportColumns.length > 0;
  
  const { theme } = useTheme();
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const buildMemberParams = () => {
      const { search, region, status, category } = filters;
      return {
        search: search || undefined,
        region_id: region || undefined,
        membership_status: status || undefined,
        member_category_id: category || undefined,
        category_id: category || undefined,
        // Request a large page size from the backend so the "All Members"
        // view really sees all members, and we only paginate on the frontend.
        per_page: 1000,
      };
    };

    const memberParams = buildMemberParams();

    const fetchData = async () => {
      try {
        setLoading(true);
        const [membersRes, regionsRes, categoriesRes] = await Promise.all([
          membersAPI.getAll(memberParams),
          regionsAPI.getAll(),
          memberCategoriesAPI.getAll()
        ]);

        const membersPayload = membersRes.data;
        const extractedMembers = Array.isArray(membersPayload?.data)
          ? membersPayload.data
          : Array.isArray(membersPayload)
            ? membersPayload
            : membersPayload?.members ?? [];

        setMembers(extractedMembers);
        setRegions(regionsRes.data || regionsRes || []);
        setCategories(categoriesRes.data || categoriesRes || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [filters]);

  const totalPages = useMemo(() => {
    if (!members || members.length === 0) return 1;
    return Math.max(1, Math.ceil(members.length / pageSize));
  }, [members, pageSize]);

  const paginatedMembers = useMemo(() => {
    if (!members || members.length === 0) return [];
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return members.slice(startIndex, endIndex);
  }, [members, currentPage, pageSize]);

  const buildExportParams = () => {
    const { search, region, status, category } = filters;
    return {
      search: search || undefined,
      region_id: region || undefined,
      membership_status: status || undefined,
      category_id: category || undefined,
      member_category_id: category || undefined,
    };
  };
  
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Reset to first page whenever filters change
    setCurrentPage(1);
  };
  
  const handleMemberClick = async (member) => {
    setShowModal(true);
    setModalLoading(true);
    try {
      const response = await membersAPI.getMember(member.id);
      setSelectedMember(response.data);
    } catch (error) {
      console.error('Error fetching member details:', error);
      setSelectedMember(member);
    } finally {
      setModalLoading(false);
    }
  };
  
  const navigateToCompleteProfile = (memberId) => {
    navigate(`/app/members/${memberId}/complete`);
  };

  const trainingNames = selectedMember?.trainings?.map(training => training.name || training.title)?.filter(Boolean) || [];
  const eventNames = selectedMember?.events?.map(event => event.title || event.name)?.filter(Boolean) || [];

  const handleApprove = async (memberId, options = {}) => {
    try {
      await membersAPI.approve(memberId, {});
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, approval_status: 'approved' } : m
      ));
      setSelectedMember(prev => prev ? { 
        ...prev, 
        approval_status: 'approved',
        membership_status: 'active',
        membership_date: prev.membership_date || new Date().toISOString()
      } : prev);
      if (!options.keepModalOpen) {
      setShowModal(false);
      }
      toast.success('Member approved and activated');
    } catch (error) {
      console.error('Error approving member:', error);
      toast.error(error.response?.data?.message || 'Failed to approve member');
    }
  };
  
  const handleReject = async (memberId) => {
    try {
    await membersAPI.reject(memberId, { rejection_reason: 'Rejected by administrator' });
      setMembers(prev => prev.map(m => 
        m.id === memberId ? { ...m, approval_status: 'rejected' } : m
      ));
    setSelectedMember(prev => prev ? {
      ...prev,
      approval_status: 'rejected',
      membership_status: prev.membership_status
    } : prev);
    toast.success('Member rejected');
      setShowModal(false);
    } catch (error) {
      console.error('Error rejecting member:', error);
    toast.error(error.response?.data?.message || 'Failed to reject member');
  }
};

const openResubmitDialog = (member) => {
  setResubmittingMember(member);
  setResubmitNotes('');
  setShowResubmitDialog(true);
};

const submitResubmissionRequest = async () => {
  if (!resubmittingMember || isResubmitting) return;

  const notes = resubmitNotes.trim();
  if (!notes) {
    toast.error('Please provide notes before sending the resubmission request.');
    return;
  }

  try {
    setIsResubmitting(true);
    await membersAPI.requestResubmission(resubmittingMember.id, { notes });
    setMembers(prev => prev.map(m =>
      m.id === resubmittingMember.id ? { ...m, approval_status: 'draft', membership_status: 'pending' } : m
    ));
    setSelectedMember(prev => prev && prev.id === resubmittingMember.id ? {
      ...prev,
      approval_status: 'draft',
      membership_status: 'pending'
    } : prev);
    toast.success('Resubmission requested.');
    setShowResubmitDialog(false);
    setResubmittingMember(null);
    setResubmitNotes('');
  } catch (error) {
    console.error('Error requesting resubmission:', error);
    toast.error(error.response?.data?.message || 'Failed to request resubmission');
  } finally {
    setIsResubmitting(false);
  }
};

  const getInitials = (firstName, lastName) => {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();
    if (!first && !last) return '?';
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  };

  const renderMemberCell = (row) => {
    const photoUrl = getFileUrl(row.profile_photo_url);
    return (
      <AvatarCell>
        {photoUrl ? (
          <AvatarImage src={photoUrl} alt={`${row.first_name} ${row.last_name}`} />
        ) : (
          <AvatarFallback>
            {getInitials(row.first_name, row.last_name)}
          </AvatarFallback>
        )}
        <AvatarDetails theme={theme}>
          <span className="name">{`${row.first_name} ${row.last_name}`.trim() || 'Unnamed Member'}</span>
          <span className="meta">{row.member_id ? `ID: ${row.member_id}` : 'ID not assigned'}</span>
        </AvatarDetails>
      </AvatarCell>
    );
  };

  const toggleMembershipStatus = async (member, keepModalOpen = false) => {
    if (!member) return;
    const nextStatus = member.membership_status === 'active' ? 'inactive' : 'active';
    try {
      const response = await membersAPI.setStatus(member.id, { status: nextStatus });
      const updatedMember = response.data?.data || response.data || member;

      setMembers(prev => prev.map(m => m.id === member.id
        ? { ...m, membership_status: updatedMember.membership_status ?? nextStatus }
        : m
      ));

      if (selectedMember?.id === member.id) {
        setSelectedMember(prev => prev ? ({
          ...prev,
          membership_status: updatedMember.membership_status ?? nextStatus,
          membership_date: updatedMember.membership_date ?? prev.membership_date,
        }) : prev);
      }

      if (!keepModalOpen) {
        toast.success(`Member marked as ${nextStatus === 'active' ? 'active' : 'inactive'}.`);
      }
    } catch (error) {
      console.error('Failed to update membership status', error);
      toast.error(error.response?.data?.message || 'Failed to update membership status');
    }
  };

  const handleExport = async (format, columnsOverride) => {
    const columnsToUse = columnsOverride && columnsOverride.length ? columnsOverride : selectedExportColumns;
    if (!columnsToUse.length) {
      toast.error('Select at least one column to export.');
      return false;
    }

    const key = format === 'pdf' ? 'pdf' : 'excel';
    setExporting(prev => ({ ...prev, [key]: true }));
    let success = false;

    try {
      const params = {
        ...buildExportParams(),
        selected_fields: columnsToUse,
      };

      const response = await reportsAPI.exportMembers(format, params);
      
      // Check if response is actually a blob (PDF/Excel) or an error
      let blob;
      if (response.data instanceof Blob) {
        blob = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object, it might be an error response
        try {
          const errorText = await new Response(response.data).text();
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || 'Export failed');
        } catch (e) {
          if (e instanceof Error && e.message !== 'Export failed') {
            throw e;
          }
          // If not JSON, try to create blob from the data
          blob = new Blob([response.data], { 
            type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
        }
      } else {
        // Fallback: create blob from response data
        blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }

      // Verify blob is not empty
      if (blob.size === 0) {
        throw new Error('Export file is empty. Please try again.');
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = `members_${format}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Members exported as ${format === 'pdf' ? 'PDF' : 'Excel'}`);
      success = true;
    } catch (error) {
      console.error('Failed to export members', error);
      
      // Try to extract error message from blob response
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorJson = JSON.parse(errorText);
          toast.error(errorJson.message || 'Failed to export members');
        } catch (e) {
          toast.error('Failed to export members. Please check the console for details.');
        }
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to export members');
      }
    } finally {
      setExporting(prev => ({ ...prev, [key]: false }));
    }

    return success;
  };
  
  const columns = [
    {
      header: 'Member',
      accessor: 'profile_photo_url',
      render: (_value, row) => renderMemberCell(row),
      width: '240px'
    },
    {
      header: 'Email',
      accessor: 'email'
    },
    {
      header: 'Region',
      accessor: 'region_id',
      render: (value) => regions.find(r => r.id === value)?.name || 'Unknown'
    },
    {
      header: 'Status',
      accessor: 'membership_status',
      render: (value) => <StatusBadge theme={theme} status={value}>{formatMembershipStatus(value)}</StatusBadge>
    },
    {
      header: 'Approval',
      accessor: 'approval_status',
      render: (value) => <ApprovalBadge theme={theme} status={value}>{formatApprovalLabel(value)}</ApprovalBadge>
    },
    {
      header: 'Actions',
      accessor: 'id',
      render: (value, row) => (
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/app/members/${row.id}`);
            }}
            leftIcon={<Eye size={14} />}
          >
            View
          </Button>
          {hasPermission('approve_members') && row.approval_status === 'approved' && (
            <Button
              size="small"
              variant={row.membership_status === 'active' ? 'danger' : 'secondary'}
              onClick={(e) => {
                e.stopPropagation();
                toggleMembershipStatus(row);
              }}
              leftIcon={row.membership_status === 'active' ? <XCircle size={14} /> : <CheckCircle size={14} />}
            >
              {row.membership_status === 'active' ? 'Set Inactive' : 'Activate'}
            </Button>
          )}
          {hasPermission('edit_members') && (
            <Button
              size="small"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                navigateToCompleteProfile(row.id);
              }}
              leftIcon={<Edit size={14} />}
            >
              Edit
            </Button>
          )}
        </div>
      )
    }
  ];
  
  return (
    <MembersContainer theme={theme}>
      <Header theme={theme}>
        <Title theme={theme}>
          Members
          <HelpTooltip>
            <p>Use this page to search, review, and manage all members in the network.</p>
            <ul>
              <li><strong>Filters</strong>: narrow members by region, membership status, and category.</li>
              <li><strong>View</strong>: open a member’s profile to see full details and history.</li>
              <li><strong>Approve / Resubmit / Reject</strong>: decide on pending memberships from the member detail dialog.</li>
              <li><strong>Activate / Set Inactive</strong>: control whether an approved member is currently active.</li>
            </ul>
          </HelpTooltip>
        </Title>
        <HeaderActions>
          <ExportActions>
            <Button
              variant="outline"
              leftIcon={<FileSpreadsheet size={16} />}
              onClick={() => setShowExportModal(true)}
            >
              Export
            </Button>
          </ExportActions>
        {hasPermission('add_members') && (
          <Button 
            variant="primary" 
            leftIcon={<Plus size={16} />}
            onClick={() => navigate('/app/members/add')}
          >
            Add Member
          </Button>
        )}
        </HeaderActions>
      </Header>
      
      <FilterCard theme={theme}>
        <FilterRow theme={theme}>
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Search</FilterLabel>
            <Input
              placeholder="Search members..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              leftIcon={<Search size={16} />}
            />
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Region</FilterLabel>
            <Select
              theme={theme}
              value={filters.region}
              onChange={(e) => handleFilterChange('region', e.target.value)}
            >
              <option value="">All Regions</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </Select>
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Status</FilterLabel>
            <Select
              theme={theme}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </Select>
          </FilterGroup>
          
          <FilterGroup theme={theme}>
            <FilterLabel theme={theme}>Category</FilterLabel>
            <Select
              theme={theme}
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </Select>
          </FilterGroup>
        </FilterRow>
      </FilterCard>
      
      <Table
        columns={columns}
        data={paginatedMembers}
        loading={loading}
        onRowClick={handleMemberClick}
        emptyMessage="No members found"
      />
      
      {!loading && members.length > 0 && (
        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div style={{ fontSize: '0.9rem', color: theme.colors.text.secondary }}>
            Showing {(currentPage - 1) * pageSize + 1}
            {' - '}
            {Math.min(currentPage * pageSize, members.length)}
            {' of '}
            {members.length} members
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <Button
              variant="outline"
              size="small"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            >
              Previous
            </Button>
            <span style={{ fontSize: '0.9rem', color: theme.colors.text.secondary }}>
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="small"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            >
              Next
            </Button>
          </div>
        </div>
      )}
      
      <Modal
        isOpen={showExportModal}
        onClose={() => {
          if (!exporting.excel && !exporting.pdf) {
            setShowExportModal(false);
          }
        }}
        title="Export Members"
        size="medium"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
            <Button variant="ghost" size="small" onClick={selectAllExportColumns}>
              Select All
            </Button>
            <Button variant="ghost" size="small" onClick={clearExportColumns}>
              Clear
            </Button>
            <span style={{ fontSize: '0.85rem', color: theme.colors.text.secondary }}>
              {selectedExportColumns.length} of {MEMBER_COLUMN_OPTIONS.length} columns selected
            </span>
          </div>
          <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem' }}>
            {MEMBER_COLUMN_OPTIONS.map(option => (
              <label
                key={option.value}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem', fontSize: '0.95rem' }}
              >
                <input
                  type="checkbox"
                  checked={selectedExportColumns.includes(option.value)}
                  onChange={() => toggleExportColumn(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button
              variant="outline"
              onClick={() => {
                if (!exporting.excel && !exporting.pdf) {
                  setShowExportModal(false);
                }
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              leftIcon={<FileText size={16} />}
              disabled={!hasSelectedExportColumns || exporting.pdf}
              onClick={async () => {
                const success = await handleExport('pdf', selectedExportColumns);
                if (success) {
                  setShowExportModal(false);
                }
              }}
            >
              {exporting.pdf ? 'Exporting...' : 'Export PDF'}
            </Button>
            <Button
              variant="primary"
              leftIcon={<FileSpreadsheet size={16} />}
              disabled={!hasSelectedExportColumns || exporting.excel}
              onClick={async () => {
                const success = await handleExport('excel', selectedExportColumns);
                if (success) {
                  setShowExportModal(false);
                }
              }}
            >
              {exporting.excel ? 'Exporting...' : 'Export Excel'}
            </Button>
          </div>
        </div>
      </Modal>
      
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={selectedMember ? `${selectedMember.first_name} ${selectedMember.last_name}` : ''}
        size="large"
      >
        {selectedMember && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowModal(false);
                  navigate(`/app/members/${selectedMember.id}`);
                  }}
                >
                View Full Profile
                </Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <strong>Member ID:</strong> {selectedMember.member_id}
              </div>
              <div>
                <strong>Email:</strong> {selectedMember.email}
              </div>
              <div>
                <strong>Phone:</strong> {selectedMember.phone}
              </div>
              <div>
                <strong>Region:</strong> {regions.find(r => r.id === selectedMember.region_id)?.name}
              </div>
              <div>
                <strong>Organization:</strong> {selectedMember.organization_name}
              </div>
              <div>
                <strong>Position:</strong> {selectedMember.position_title}
              </div>
            </div>
            
            <div>
              <strong>Thematic Areas:</strong> {selectedMember.thematic_areas?.join(', ')}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              {hasPermission('approve_members') && ['draft', 'pending', 'regional_pending', 'central_pending'].includes(selectedMember.approval_status) && (
                <>
                  <Button
                    variant="danger"
                    onClick={() => handleReject(selectedMember.id)}
                    leftIcon={<XCircle size={16} />}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="warning"
                    onClick={() => openResubmitDialog(selectedMember)}
                    leftIcon={<RotateCcw size={16} />}
                  >
                    Resubmit
                  </Button>
                  <Button
                    variant="primary"
                    onClick={() => handleApprove(selectedMember.id)}
                    leftIcon={<CheckCircle size={16} />}
                  >
                    Approve
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showResubmitDialog}
        onClose={() => {
          if (!isResubmitting) {
            setShowResubmitDialog(false);
            setResubmittingMember(null);
            setResubmitNotes('');
          }
        }}
        title="Request Resubmission"
        size="small"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ margin: 0, color: theme.colors.text.secondary }}>
            Provide clear guidance so the applicant knows what to update before resubmitting.
          </p>
          <textarea
            value={resubmitNotes}
            onChange={(e) => setResubmitNotes(e.target.value)}
            placeholder="Example: Please upload a clearer ID scan and fill the organization field."
            rows={4}
            style={{
              width: '100%',
              padding: '0.75rem',
              borderRadius: '8px',
              border: `1px solid ${theme.colors.border.medium}`,
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.base,
              resize: 'vertical'
            }}
            disabled={isResubmitting}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
            <Button
              variant="secondary"
              onClick={() => {
                if (!isResubmitting) {
                  setShowResubmitDialog(false);
                  setResubmittingMember(null);
                  setResubmitNotes('');
                }
              }}
              disabled={isResubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={submitResubmissionRequest}
              disabled={isResubmitting}
            >
              {isResubmitting ? 'Sending...' : 'Send Request'}
            </Button>
          </div>
        </div>
      </Modal>
    </MembersContainer>
  );
};

export default Members;
